import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ClipboardList, Building2, BarChart3, Users, ArrowRight, Smartphone, Menu, X, CheckCircle2, UtensilsCrossed, TrendingUp, Clock, ShieldCheck, Send, Phone, Mail, Building, UserCircle, MessageSquare, Star, Quote, Truck, CreditCard } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { addDemoRequest } from '../utils/demoRequests';
function AnimatedSection({
  children,
  className,
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay: delay,
    ease: 'easeOut'
  }} className={className} data-id="element-3792">
      {children}
    </motion.div>;
}
const features = [{
  icon: <ClipboardList className="w-6 h-6" data-id="element-3793" />,
  title: 'Pedidos Centralizados',
  description: 'Tus empleados eligen su almuerzo desde cualquier dispositivo. Tú recibes un resumen consolidado sin llamadas ni Excel.'
}, {
  icon: <Smartphone className="w-6 h-6" data-id="element-3794" />,
  title: 'Menú Digital Diario',
  description: 'Menú actualizado cada día con fotos, descripciones y opciones personalizables para cada gusto y restricción alimentaria.'
}, {
  icon: <CreditCard className="w-6 h-6" data-id="element-3795" />,
  title: 'Subsidios Automáticos',
  description: 'Define cuánto subsidia tu empresa por empleado. Cricket calcula el descuento automáticamente en cada pedido.'
}, {
  icon: <Truck className="w-6 h-6" data-id="element-3796" />,
  title: 'Entrega Puntual',
  description: 'Almuerzos etiquetados y entregados a tiempo en tu oficina. Cada pedido llega identificado con el nombre del empleado.'
}, {
  icon: <BarChart3 className="w-6 h-6" data-id="element-3797" />,
  title: 'Reportes de Consumo',
  description: 'Visualiza cuánto gasta tu empresa en alimentación, qué platos prefieren tus empleados y optimiza tu presupuesto.'
}, {
  icon: <Users className="w-6 h-6" data-id="element-3798" />,
  title: 'Multi-Sede',
  description: 'Gestiona varias oficinas o sucursales desde un solo panel. Cada sede con su menú, horarios y reglas de subsidio.'
}];
const steps = [{
  number: '01',
  title: 'Configuración',
  description: 'Registramos tu empresa, configuramos los subsidios y cargamos el menú disponible.'
}, {
  number: '02',
  title: 'Pedido',
  description: 'Tus empleados piden su almuerzo desde la App o Web antes de la hora de corte.'
}, {
  number: '03',
  title: 'Entrega Puntual',
  description: 'Cricket gestiona la producción y logística para entregar los almuerzos listos para comer.'
}];
const stats = [{
  value: '—',
  label: 'Empresas activas',
  icon: <Building2 className="w-5 h-5" data-id="element-3799" />
}, {
  value: '—',
  label: 'Pedidos mensuales',
  icon: <TrendingUp className="w-5 h-5" data-id="element-3800" />
}, {
  value: '—',
  label: 'Uptime garantizado',
  icon: <ShieldCheck className="w-5 h-5" data-id="element-3801" />
}, {
  value: '—',
  label: 'Tiempo de setup',
  icon: <Clock className="w-5 h-5" data-id="element-3802" />
}];
const testimonials = [{
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}, {
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}, {
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}];
export function LandingPage() {
  useDocumentTitle('Inicio');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    message: ''
  });
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      // Guardar en Supabase (o localStorage como fallback)
      await addDemoRequest(demoForm);
      // ── Enviar formulario via Web3Forms (gratis, sin backend) ──
      const WEB3FORMS_KEY = 'TU_ACCESS_KEY_AQUI';
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            access_key: WEB3FORMS_KEY,
            subject: `🍽️ Nueva solicitud de Demo - ${demoForm.company}`,
            from_name: 'Cricket - Landing Page',
            name: demoForm.name,
            email: demoForm.email,
            empresa: demoForm.company,
            telefono: demoForm.phone,
            cantidad_empleados: demoForm.employees,
            mensaje: demoForm.message || 'Sin mensaje adicional'
          })
        });
        const result = await response.json();
        if (!result.success) {
          console.error('Web3Forms error:', result);
        }
      } catch (webError) {
        console.error('Web3Forms fetch error:', webError);
      }
      setDemoSubmitted(true);
    } catch (error) {
      console.error('Error enviando formulario:', error);
      setDemoSubmitted(true);
    } finally {
      setDemoLoading(false);
    }
  };
  return <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900 selection:bg-gray-900 selection:text-white scroll-smooth" data-id="element-3803">
      {/* ───── Navbar ───── */}
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm transition-all" data-id="element-3804">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between" data-id="element-3805">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-3806">
            <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-9 w-auto object-contain transition-transform group-hover:scale-105" data-id="element-3807" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-0.5" data-id="element-3808">
            <a href="#features" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3809">
              Características
            </a>
            <a href="#how-it-works" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3810">
              Cómo funciona
            </a>
            <a href="#demo" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3811">
              Agendar Demo
            </a>
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-1.5" data-id="element-3812">
            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-3813" />
            <Link to="/login" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all text-sm font-medium" data-id="element-3814">
              Iniciar Sesión
            </Link>
            <motion.a whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} href="#demo" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-all shadow-sm" data-id="element-3815">
              Agendar Demo
            </motion.a>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-all" data-id="element-3816">
            {isMobileMenuOpen ? <X className="w-5 h-5" data-id="element-3817" /> : <Menu className="w-5 h-5" data-id="element-3818" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="md:hidden border-t border-gray-100 bg-white p-4 flex flex-col gap-1 shadow-lg" data-id="element-3819">
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3820">
              Características
            </a>
            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3821">
              Cómo funciona
            </a>
            <a href="#demo" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3822">
              Agendar Demo
            </a>
            <div className="h-px bg-gray-100 my-1" data-id="element-3823" />
            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3824">
              Iniciar Sesión
            </Link>
            <a href="#demo" onClick={() => setIsMobileMenuOpen(false)} className="w-full text-center py-2.5 px-4 text-sm font-semibold rounded-xl text-white bg-gray-900 hover:bg-gray-800 transition-colors" data-id="element-3825">
              Agendar Demo
            </a>
          </motion.div>}
      </motion.nav>

      {/* ───── Hero ───── */}
      <section className="relative pt-10 pb-16 lg:pt-28 lg:pb-32 overflow-hidden" data-id="element-3826">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_top_right,_#f0fdf4_0%,_#f9fafb_50%,_white_100%)]" data-id="element-3827"></div>
        <div className="absolute top-0 right-0 -z-10 w-[300px] lg:w-[600px] h-[300px] lg:h-[600px] bg-emerald-100/40 rounded-full blur-3xl opacity-60 pointer-events-none" data-id="element-3828"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-[200px] lg:w-[400px] h-[200px] lg:h-[400px] bg-gray-100/60 rounded-full blur-3xl pointer-events-none" data-id="element-3829"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3830">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center" data-id="element-3831">
            <div className="text-center lg:text-left" data-id="element-3832">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 text-xs font-bold uppercase tracking-wide mb-6" data-id="element-3833">
                <span className="relative flex h-2 w-2" data-id="element-3834">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-3835"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-3836"></span>
                </span>
                Plataforma Corporativa
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 mb-6 leading-[1.08]" data-id="element-3837">
                Almuerzos corporativos, <br className="hidden sm:block" data-id="element-3838" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500" data-id="element-3839">
                  sin complicaciones.
                </span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg sm:text-xl text-gray-500 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-3840">
                Olvídate de la logística. Cricket centraliza pedidos, subsidios
                y entregas para que tu equipo disfrute su comida y tú recuperes
                tu tiempo.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4" data-id="element-3841">
                <motion.a whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} href="#demo" className="w-full sm:w-auto px-7 py-3.5 sm:px-8 sm:py-4 text-base font-bold rounded-xl text-white bg-gray-900 hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group" data-id="element-3842">
                  Agendar Demo Gratis
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-3843" />
                </motion.a>
                <motion.div whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full sm:w-auto" data-id="element-3844">
                  <Link to="/login" className="w-full sm:w-auto px-7 py-3.5 sm:px-8 sm:py-4 text-base font-bold rounded-xl text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all shadow-sm hover:shadow-md flex items-center justify-center" data-id="element-3845">
                    Ya tengo cuenta
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="mt-8 lg:mt-10 flex items-center justify-center lg:justify-start gap-4 text-sm text-gray-500" data-id="element-3846">
                <div className="flex -space-x-2" data-id="element-3847">
                  {['MR', 'CM', 'AP', 'JL'].map((initials, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-900 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-3848">
                      {initials}
                    </div>)}
                </div>
                <p data-id="element-3849">
                  <span className="font-bold text-gray-900" data-id="element-3850">— empresas</span> ya
                  confían en Cricket
                </p>
              </motion.div>
            </div>

            {/* Mockup - Visible on ALL screen sizes */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="relative flex justify-center lg:block" data-id="element-3851">
              {/* Background circle - smaller on mobile */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[350px] lg:w-[500px] h-[280px] sm:h-[350px] lg:h-[500px] bg-gradient-to-br from-gray-100 to-gray-50 rounded-full border border-gray-100/50" data-id="element-3852"></div>

              {/* Phone mockup card - responsive sizing */}
              <div className="relative z-10 bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-6 w-[260px] sm:w-[300px] lg:max-w-sm mx-auto transform lg:-rotate-2 lg:hover:rotate-0 transition-transform duration-500" data-id="element-3853">
                <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-3854">
                  <div data-id="element-3855">
                    <h3 className="font-bold text-base sm:text-lg" data-id="element-3856">
                      Hola, Carlos 👋
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-400" data-id="element-3857">
                      Subsidio:{' '}
                      <span className="text-green-600 font-semibold" data-id="element-3858">
                        RD$ 350.00
                      </span>
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-3859">
                    <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800" data-id="element-3860" />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3 mb-2.5 sm:mb-3 flex gap-2.5 sm:gap-3 border border-gray-100" data-id="element-3861">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center text-xl sm:text-2xl" data-id="element-3862">
                    🍗
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3863">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate" data-id="element-3864">
                      Pollo al Horno
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-gray-400" data-id="element-3865">
                      Con arroz y ensalada
                    </p>
                    <div className="flex justify-between items-center mt-1 sm:mt-1.5" data-id="element-3866">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-3867">
                        RD$ 250.00
                      </span>
                      <span className="px-1.5 sm:px-2 py-0.5 bg-green-100 text-green-700 text-[8px] sm:text-[10px] font-bold rounded-full" data-id="element-3868">
                        Subsidiado
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-2.5 sm:p-3 mb-3 sm:mb-4 flex gap-2.5 sm:gap-3 border border-gray-100 shadow-sm" data-id="element-3869">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center text-xl sm:text-2xl" data-id="element-3870">
                    🥩
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3871">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate" data-id="element-3872">
                      Res Guisada
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-gray-400" data-id="element-3873">
                      Con habichuelas y plátano
                    </p>
                    <div className="flex justify-between items-center mt-1 sm:mt-1.5" data-id="element-3874">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-3875">
                        RD$ 320.00
                      </span>
                      <button className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-900 rounded-lg text-white text-[9px] sm:text-[11px] font-bold" data-id="element-3876">
                        Agregar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-3 sm:pt-4 border-t border-gray-100" data-id="element-3877">
                  <div className="flex justify-between mb-2.5 sm:mb-3" data-id="element-3878">
                    <span className="text-xs sm:text-sm text-gray-500" data-id="element-3879">
                      Total
                    </span>
                    <span className="text-xs sm:text-sm font-bold" data-id="element-3880">
                      RD$ 250.00
                    </span>
                  </div>
                  <button className="w-full py-2.5 sm:py-3 bg-gray-900 text-white rounded-xl text-xs sm:text-sm font-bold shadow-lg shadow-gray-900/20" data-id="element-3881">
                    Confirmar Pedido →
                  </button>
                </div>
              </div>

              {/* Floating cards - hidden on small mobile, visible from sm up */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6,
              duration: 0.5
            }} className="absolute -bottom-4 -right-2 sm:-right-4 bg-white p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 flex items-center gap-2 sm:gap-3 hidden sm:flex" style={{
              animation: 'float 3s ease-in-out infinite'
            }} data-id="element-3882">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-3883">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" data-id="element-3884" />
                </div>
                <div data-id="element-3885">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 font-medium" data-id="element-3886">
                    Ahora mismo
                  </p>
                  <p className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-3887">
                    Pedido Entregado ✓
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.8,
              duration: 0.5
            }} className="absolute -top-2 sm:-top-4 -left-2 sm:-left-4 bg-white p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 flex items-center gap-2 sm:gap-3 hidden sm:flex" style={{
              animation: 'float 3s ease-in-out infinite 1.5s'
            }} data-id="element-3888">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600" data-id="element-3889">
                  <TrendingUp className="w-3.5 h-3.5 sm:w-5 sm:h-5" data-id="element-3890" />
                </div>
                <div data-id="element-3891">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 font-medium" data-id="element-3892">
                    Hoy
                  </p>
                  <p className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-3893">
                    127 pedidos
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── Stats Strip ───── */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50" data-id="element-3894">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3895">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-3896">
            {stats.map((stat, i) => <AnimatedSection key={i} delay={i * 0.1} className="text-center" data-id="element-3897">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-600 mb-3" data-id="element-3898">
                  {stat.icon}
                </div>
                <p className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-3899">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3900">{stat.label}</p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Features ───── */}
      <section id="features" className="py-16 lg:py-24 bg-white relative" data-id="element-3901">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_40%_at_bottom_left,_#f0fdf4_0%,_white_60%)]" data-id="element-3902"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3903">
          <AnimatedSection className="text-center mb-12 lg:mb-16 max-w-3xl mx-auto" data-id="element-3904">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide mb-4" data-id="element-3905">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3906"></span>
              Funcionalidades
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 tracking-tight" data-id="element-3907">
              Todo lo que necesitas para tu comedor
            </h2>
            <p className="text-base sm:text-lg text-gray-500" data-id="element-3908">
              Herramientas específicas para resolver los dolores de cabeza de
              RRHH y Operaciones.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-id="element-3909">
            {features.map((feature, index) => <AnimatedSection key={index} delay={index * 0.08} className="group p-7 rounded-2xl bg-white border border-gray-100 hover:border-emerald-100 shadow-sm hover:shadow-xl hover:shadow-emerald-900/5 hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden" data-id="element-3910">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/60 group-hover:to-transparent transition-all duration-300 pointer-events-none" data-id="element-3911" />
                <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-700 flex items-center justify-center mb-5 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-3912">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2.5" data-id="element-3913">
                  {feature.title}
                </h3>
                <p className="text-gray-500 leading-relaxed text-sm" data-id="element-3914">
                  {feature.description}
                </p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Testimonials ───── */}
      <section className="py-16 lg:py-24 bg-gray-50/80 border-y border-gray-100" data-id="element-3915">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3916">
          <AnimatedSection className="text-center mb-12 lg:mb-16" data-id="element-3917">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wide mb-4 shadow-sm" data-id="element-3918">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" data-id="element-3919" />
              Testimonios
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight" data-id="element-3920">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-base sm:text-lg text-gray-500" data-id="element-3921">
              Empresas reales, resultados reales.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6" data-id="element-3922">
            {testimonials.map((t, i) => <AnimatedSection key={i} delay={i * 0.1} className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm relative flex flex-col" data-id="element-3923">
                <div className="flex gap-0.5 mb-4" data-id="element-3924">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-3925" />)}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 text-[15px] flex-1" data-id="element-3926">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100" data-id="element-3927">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-xs font-bold shrink-0" data-id="element-3928">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div data-id="element-3929">
                    <p className="text-sm font-bold text-gray-900" data-id="element-3930">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-3931">
                      {t.role} · {t.company}
                    </p>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── How It Works ───── */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-gray-900 text-white relative overflow-hidden" data-id="element-3932">
        <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:48px_48px]" data-id="element-3933"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3934">
          <AnimatedSection className="flex flex-col md:flex-row md:items-end justify-between mb-12 lg:mb-16 gap-6" data-id="element-3935">
            <div data-id="element-3936">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-id="element-3937">
                Así funciona Cricket
              </h2>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl" data-id="element-3938">
                Implementación rápida. Resultados inmediatos.
              </p>
            </div>
            <a href="#demo" className="inline-flex items-center text-white hover:text-gray-300 font-semibold border-b border-white hover:border-gray-300 pb-1 transition-colors" data-id="element-3939">
              Agendar una demo <ArrowRight className="ml-2 w-4 h-4" data-id="element-3940" />
            </a>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 relative" data-id="element-3941">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700 border-t border-dashed border-gray-600" data-id="element-3942"></div>
            {steps.map((step, index) => <AnimatedSection key={index} delay={index * 0.2} className="relative group" data-id="element-3943">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-800 rounded-xl sm:rounded-2xl border border-gray-700 flex items-center justify-center text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 relative z-10 group-hover:bg-white group-hover:text-black transition-colors duration-300 shadow-lg shadow-black/50" data-id="element-3944">
                  {step.number}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4" data-id="element-3945">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-3946">
                  {step.description}
                </p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Demo Form ───── */}
      <section id="demo" className="py-16 lg:py-24 bg-white relative overflow-hidden" data-id="element-3947">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-50/50 rounded-full blur-3xl -mr-48 -mt-48 pointer-events-none" data-id="element-3948"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gray-100/50 rounded-full blur-3xl -ml-48 -mb-48 pointer-events-none" data-id="element-3949"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3950">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-3951">
            {/* Left - Info */}
            <AnimatedSection data-id="element-3952">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold uppercase tracking-wide mb-6" data-id="element-3953">
                <Phone className="w-3.5 h-3.5" data-id="element-3954" />
                Demo personalizada
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-tight" data-id="element-3955">
                Agenda una demo
                <br data-id="element-3956" />
                <span className="text-gray-400" data-id="element-3957">sin compromiso</span>
              </h2>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed" data-id="element-3958">
                Te mostramos cómo Cricket puede transformar la gestión de
                almuerzos en tu empresa. La demo dura ~20 minutos y es 100%
                personalizada a tu caso.
              </p>

              <div className="space-y-5" data-id="element-3959">
                {[{
                title: 'Demo en vivo de la plataforma',
                desc: 'Verás exactamente cómo funciona con datos de ejemplo.'
              }, {
                title: 'Propuesta personalizada',
                desc: 'Precio adaptado al tamaño de tu empresa y necesidades.'
              }, {
                title: 'Sin compromiso',
                desc: 'Explora Cricket sin presión. Decide cuando estés listo.'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-4" data-id="element-3960">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-3961">
                      <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-3962" />
                    </div>
                    <div data-id="element-3963">
                      <p className="font-semibold text-gray-900" data-id="element-3964">
                        {item.title}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3965">{item.desc}</p>
                    </div>
                  </motion.div>)}
              </div>

              <div className="mt-10 p-5 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-3966">
                <p className="text-sm text-gray-500 mb-2" data-id="element-3967">
                  ¿Prefieres contactarnos directamente?
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-3968">
                  <a href="https://wa.me/18091234567" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-green-700 transition-colors" data-id="element-3969">
                    <Phone className="w-4 h-4" data-id="element-3970" /> —
                  </a>
                  <a href="mailto:ventas@cricket.do" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-blue-700 transition-colors" data-id="element-3971">
                    <Mail className="w-4 h-4" data-id="element-3972" /> —
                  </a>
                </div>
              </div>
            </AnimatedSection>

            {/* Right - Form */}
            <AnimatedSection delay={0.2} className="bg-white rounded-3xl border border-gray-200 shadow-xl p-8 lg:p-10" data-id="element-3973">
              {demoSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-3974">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3975">
                    <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-3976" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-3977">
                    ¡Solicitud enviada!
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto" data-id="element-3978">
                    Nuestro equipo se pondrá en contacto contigo en las próximas
                    24 horas para coordinar tu demo.
                  </p>
                  <button onClick={() => {
                setDemoSubmitted(false);
                setDemoForm({
                  name: '',
                  email: '',
                  company: '',
                  phone: '',
                  employees: '',
                  message: ''
                });
              }} className="text-sm font-semibold text-gray-900 hover:underline underline-offset-2" data-id="element-3979">
                    Enviar otra solicitud
                  </button>
                </motion.div> : <>
                  <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-3980">
                    Solicita tu demo
                  </h3>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-3981">
                    Completa el formulario y te contactaremos pronto.
                  </p>

                  <form onSubmit={handleDemoSubmit} className="space-y-4" data-id="element-3982">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3983">
                      <div data-id="element-3984">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-3985">
                          Nombre completo
                        </label>
                        <div className="relative" data-id="element-3986">
                          <UserCircle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-3987" />
                          <input type="text" required value={demoForm.name} onChange={e => setDemoForm({
                        ...demoForm,
                        name: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="Juan Pérez" data-id="element-3988" />
                        </div>
                      </div>
                      <div data-id="element-3989">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-3990">
                          Email corporativo
                        </label>
                        <div className="relative" data-id="element-3991">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-3992" />
                          <input type="email" required value={demoForm.email} onChange={e => setDemoForm({
                        ...demoForm,
                        email: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="juan@empresa.com" data-id="element-3993" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3994">
                      <div data-id="element-3995">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-3996">
                          Empresa
                        </label>
                        <div className="relative" data-id="element-3997">
                          <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-3998" />
                          <input type="text" required value={demoForm.company} onChange={e => setDemoForm({
                        ...demoForm,
                        company: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="Mi Empresa SRL" data-id="element-3999" />
                        </div>
                      </div>
                      <div data-id="element-4000">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4001">
                          WhatsApp / Teléfono
                        </label>
                        <div className="relative" data-id="element-4002">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-4003" />
                          <input type="tel" required value={demoForm.phone} onChange={e => {
                        // Only allow digits, +, -, (, ), spaces
                        const cleaned = e.target.value.replace(/[^\d+\-() ]/g, '');
                        setDemoForm({
                          ...demoForm,
                          phone: cleaned
                        });
                      }} maxLength={20} pattern="[\d\+\-\(\) ]+" className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="809-123-4567" data-id="element-4004" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-4005">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4006">
                        Cantidad de empleados
                      </label>
                      <select required value={demoForm.employees} onChange={e => setDemoForm({
                    ...demoForm,
                    employees: e.target.value
                  })} className="w-full px-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all text-gray-700 appearance-none cursor-pointer" data-id="element-4007">
                        <option value="" data-id="element-4008">Selecciona un rango</option>
                        <option value="1-50" data-id="element-4009">1 – 50 empleados</option>
                        <option value="51-100" data-id="element-4010">51 – 100 empleados</option>
                        <option value="101-300" data-id="element-4011">101 – 300 empleados</option>
                        <option value="301-500" data-id="element-4012">301 – 500 empleados</option>
                        <option value="500+" data-id="element-4013">Más de 500 empleados</option>
                      </select>
                    </div>

                    <div data-id="element-4014">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4015">
                        Mensaje{' '}
                        <span className="text-gray-400 font-normal" data-id="element-4016">
                          (opcional)
                        </span>
                      </label>
                      <div className="relative" data-id="element-4017">
                        <MessageSquare className="absolute left-3.5 top-3 w-[18px] h-[18px] text-gray-400" data-id="element-4018" />
                        <textarea value={demoForm.message} onChange={e => setDemoForm({
                      ...demoForm,
                      message: e.target.value
                    })} rows={3} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400 resize-none" placeholder="Cuéntanos sobre tu necesidad..." data-id="element-4019" />
                      </div>
                    </div>

                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} type="submit" disabled={demoLoading} className="w-full h-12 bg-gray-900 text-white rounded-xl text-[15px] font-semibold shadow-lg shadow-gray-900/10 hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-4020">
                      {demoLoading ? <>
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" data-id="element-4021">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-4022" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4023" />
                          </svg>
                          Enviando...
                        </> : <>
                          <Send className="w-5 h-5" data-id="element-4024" />
                          Solicitar Demo Gratis
                        </>}
                    </motion.button>

                    <p className="text-xs text-gray-400 text-center mt-3" data-id="element-4025">
                      Al enviar, aceptas nuestra política de privacidad. No
                      spam, lo prometemos.
                    </p>
                  </form>
                </>}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ───── Final CTA ───── */}
      <section className="py-16 lg:py-24 bg-gray-950 text-white relative overflow-hidden" data-id="element-4026">
        <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear_gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:48px_48px]" data-id="element-4027"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-4028">
          <AnimatedSection data-id="element-4029">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-4030">
              ¿Listo para modernizar el almuerzo?
            </h2>
            <p className="text-base sm:text-xl text-gray-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-4031">
              Únete a las empresas que ya confían en Cricket para gestionar la
              alimentación de sus colaboradores.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4" data-id="element-4032">
              <motion.a whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} href="#demo" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold rounded-xl text-gray-900 bg-white hover:bg-gray-100 transition-all shadow-lg hover:-translate-y-1" data-id="element-4033">
                Agendar Demo
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-4034" />
              </motion.a>
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-4035">
                <Link to="/login" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold rounded-xl text-white border border-gray-700 hover:border-gray-500 hover:bg-white/5 transition-all" data-id="element-4036">
                  Iniciar Sesión
                </Link>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer data-id="element-4037" />

      <style data-id="element-4038">{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>;
}